import React, { useState } from 'react';
import { Store, TrendingUp, Users, Package, Award, DollarSign, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const PartnerPage = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    storeAddress: '',
    city: '',
    phone: '',
    email: '',
    businessType: '',
    yearsInOperation: ''
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Application Submitted!",
      description: "Thank you for your interest. Our partnership team will review your application and contact you soon."
    });
    setFormData({
      businessName: '',
      ownerName: '',
      storeAddress: '',
      city: '',
      phone: '',
      email: '',
      businessType: '',
      yearsInOperation: ''
    });
  };

  const benefits = [
    {
      icon: DollarSign,
      title: "Zero Investment",
      description: "No franchise fees or joining costs. You don't need to invest in inventory or infrastructure."
    },
    {
      icon: TrendingUp,
      title: "Positive Cash Flow",
      description: "Generate additional revenue from every shipment processed, creating a new stream of income."
    },
    {
      icon: Users,
      title: "Increase Walk-In Traffic",
      description: "Shipping services bring regular customers, increasing chance of additional purchases."
    },
    {
      icon: Package,
      title: "Easy to Operate",
      description: "Simple and user-friendly system. Training and support provided by Shippzy."
    }
  ];

  const carriers = ['DHL', 'FedEx', 'UPS', 'Purolator'];

  const eligibleBusinesses = [
    'Convenience stores',
    'Vape shops',
    'Grocery stores',
    'Mobile phone stores',
    'Print shops',
    'Gift shops',
    'Travel agencies',
    'Retail stores',
    'Business centers',
    'Small local shops'
  ];

  const providedEquipment = [
    'Professional weighing scale',
    'Label printer',
    'Shipping software access',
    'Partner account login',
    'Shipping documentation tools'
  ];

  const marketingSupport = [
    'Store branding materials',
    'Window stickers and signage',
    'Marketing posters',
    'Digital listing on Shippzy website',
    'Local promotion through Shippzy channels'
  ];

  return (
    <>
      <Helmet>
        <title>Partner With Shippzy | Become a Drop-off Partner Location Canada</title>
        <meta name="description" content="Become a Shippzy partner location in Canada. Earn revenue by offering international shipping drop-off services at your business. Join our courier network." />
        <link rel="canonical" href="https://www.shippzy.ca/partner" />
      </Helmet>
      
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_angry-ellis-7/artifacts/rd8nrfx9_IMG_7688.png" 
              alt="Shippzy Logo" 
              className="h-20 w-20 rounded-full"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Partner With Shippzy</h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Turn your store into a shipping hub and earn additional revenue with zero investment and zero franchise fees.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Become a Shippzy Partner Location</h2>
          <p className="text-lg text-gray-700 leading-relaxed text-center">
            Shippzy Inc. is expanding its logistics partner network across Canada and internationally. We invite local retail stores, convenience stores, vape shops, grocery stores, and other walk-in businesses to become official Shippzy partner drop-off and shipping locations.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mt-4 text-center">
            Our partner program allows businesses to generate additional revenue and customer foot traffic by offering shipping services powered by Shippzy. There are no franchise fees, no upfront investment, and no complicated contracts.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 via-red-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Why Partner With Shippzy</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Revenue Highlight */}
          <div className="mt-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-2xl p-8 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Earn Extra Monthly Revenue</h3>
            <p className="text-xl">
              Many partner locations generate <span className="font-bold text-yellow-200">$500–$2000+ per month</span> in additional revenue depending on shipment volume and location traffic.
            </p>
          </div>
        </div>
      </section>

      {/* Eligible Businesses */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Who Can Partner With Us</h2>
          <p className="text-lg text-gray-700 text-center mb-8">
            We welcome businesses of all sizes, including:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {eligibleBusinesses.map((business, index) => (
              <div key={index} className="flex items-center gap-2 bg-gray-50 p-4 rounded-lg hover:bg-red-50 transition-colors">
                <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-gray-800">{business}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-center mt-8">
            Partners can operate from any city or country, although businesses located in Canada and high-traffic areas are preferred.
          </p>
        </div>
      </section>

      {/* What Partners Do */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What Partners Do</h2>
          <p className="text-lg text-gray-700 text-center mb-8">
            As a Shippzy Partner Location, your store will:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Accept parcel drop-offs from customers',
              'Assist customers in measuring package dimensions',
              'Print shipping labels through our system',
              'Provide basic shipment assistance to customers',
              'Hand over shipments to the designated courier pickup'
            ].map((task, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="flex items-center gap-4 p-6">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold">{index + 1}</span>
                  </div>
                  <p className="text-gray-800">{task}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-gray-700 mt-8 text-lg">
            Partners act as local shipping service points that connect customers with Shippzy's logistics network.
          </p>
        </div>
      </section>

      {/* What Shippzy Provides */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What Shippzy Provides</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Equipment */}
            <Card className="border-2 border-red-200">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Package className="w-6 h-6 text-red-600" />
                  Equipment Provided
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {providedEquipment.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Marketing Support */}
            <Card className="border-2 border-yellow-200">
              <CardHeader className="bg-yellow-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Award className="w-6 h-6 text-yellow-600" />
                  Branding & Marketing Support
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {marketingSupport.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-gray-600 mt-4 text-sm">
                  This helps partners attract new customers to their stores.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Carrier Network */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Our Carrier Network</h2>
          <p className="text-xl text-gray-300 mb-8">
            Shippzy works with leading global courier providers to arrange shipping services:
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {carriers.map((carrier, index) => (
              <div key={index} className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-xl">
                {carrier}
              </div>
            ))}
          </div>
          <p className="text-gray-300 mt-8">
            This allows customers to compare options and choose the most suitable shipping service.
          </p>
        </div>
      </section>

      {/* No Fees Section */}
      <section className="py-16 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">No Fees or Hidden Costs</h2>
          <p className="text-xl text-gray-700 mb-8">The Shippzy partner program is designed to be simple.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'No joining fee',
              'No franchise fee',
              'No monthly fee',
              'No inventory required'
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-center gap-3 bg-white p-6 rounded-lg shadow-md">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
                <span className="text-lg font-semibold text-gray-800">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-700 mt-8 text-lg">
            Partners simply provide a drop-off and shipping assistance point, while Shippzy manages the logistics network.
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Apply to Become a Partner</h2>
          <p className="text-lg text-gray-600 text-center mb-8">
            Interested businesses can apply by submitting the form below. Our team will review your application and contact you regarding partnership approval.
          </p>

          <Card className="shadow-xl">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                    <Input
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      placeholder="ABC Convenience Store"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Owner Name *</label>
                    <Input
                      name="ownerName"
                      value={formData.ownerName}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Store Address *</label>
                  <Input
                    name="storeAddress"
                    value={formData.storeAddress}
                    onChange={handleChange}
                    required
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City / Country *</label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="Toronto, Canada"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="store@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Type *</label>
                  <Input
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    required
                    placeholder="Convenience store / vape store / retail etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Years in Operation *</label>
                  <Input
                    name="yearsInOperation"
                    value={formData.yearsInOperation}
                    onChange={handleChange}
                    required
                    placeholder="5"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-14 text-lg font-semibold"
                >
                  Submit Partnership Application
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Contact Partnership Team</h2>
          <p className="text-xl text-gray-300 mb-8">For partnership inquiries, contact:</p>
          <div className="space-y-4">
            <p className="text-lg">
              <span className="font-semibold">Email:</span> ship@shippzy.ca
            </p>
            <p className="text-lg">
              <span className="font-semibold">Phone:</span> +1 (437) 616-0758
            </p>
            <p className="text-lg">
              <span className="font-semibold">Website:</span> Shippzy.ca
            </p>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default PartnerPage;
